/**
 * What happens to the recording when the tab goes away.
 *
 * Both interview contexts had this inline and identical, and both were written
 * when there was only one transport. The legacy path has to beacon the final
 * blob itself, because `sendBeacon` is the only thing that survives unload and
 * the WebSocket is torn down synchronously. The multipart path must do the
 * opposite: the bytes belong to `S3MultipartSink`, and diverting them to Django
 * both violates "no video through the server" and silently truncates the S3
 * object, because a blob that goes to the beacon never enters the PartBuffer.
 *
 * The decision lives here rather than in the contexts so that it is one
 * decision, tested once, instead of two copies that drift.
 *
 * ON THE MULTIPART PATH WE DELIBERATELY DO NOT FINALISE.
 * `stop()` is still called, so the sink is offered the last blob and gets to
 * PUT it if the browser allows — but an unloading page cannot be relied on to
 * finish an upload, and asking the server to complete here would be worse than
 * useless: `CompleteMultipartUpload` freezes the object at whatever `ListParts`
 * returns at that instant, so a PUT still in flight would land after completion
 * and be discarded. Waiting is strictly safer. The idle sweep already completes
 * abandoned uploads from the authoritative part list once everything has
 * settled, which costs latency and never costs bytes.
 */
export const runUnloadTeardown = ({ transport, recorder, takeQueued, beaconVideo, beaconEndSignal, onDrainQueued, onFinalChunk, onStopError, }) => {
    if (recorder) {
        if (transport === 's3-multipart') {
            // Leave `ondataavailable` exactly as the SharedRecorder wired it, so the
            // final blob reaches the sink instead of the server.
            try {
                recorder.stop();
            }
            catch (err) {
                onStopError === null || onStopError === void 0 ? void 0 : onStopError(err);
            }
        }
        else {
            // Legacy, unchanged: drain what the reconnect buffered, then capture the
            // final blob. `takeQueued` removes them from the sink, so the same bytes
            // cannot be both beaconed here and re-sent by a later drain.
            const queued = takeQueued();
            if (queued.length > 0)
                onDrainQueued === null || onDrainQueued === void 0 ? void 0 : onDrainQueued(queued.length);
            queued.forEach((blob, i) => beaconVideo(blob, `retry-chunk-${i}`));
            // ondataavailable fires before onstop, so this captures the last blob
            // before the page closes.
            const originalOnData = recorder.ondataavailable;
            recorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) {
                    onFinalChunk === null || onFinalChunk === void 0 ? void 0 : onFinalChunk(e.data.size);
                    beaconVideo(e.data, 'final-chunk');
                }
                // Restore in case stop() triggers more than one event.
                recorder.ondataavailable = originalOnData;
            };
            try {
                recorder.stop();
            }
            catch (err) {
                recorder.ondataavailable = originalOnData;
                onStopError === null || onStopError === void 0 ? void 0 : onStopError(err);
            }
        }
    }
    beaconEndSignal();
};
